import React from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { googleReviews } from '../mock';

const ReviewStars = ({ rating }) => {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          size={16}
          className={star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}
        />
      ))}
    </div>
  );
};

const GoogleLogo = () => (
  <svg className="w-6 h-6" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const ReviewCard = ({ review }) => {
  const colors = ['bg-blue-100 text-blue-700', 'bg-red-100 text-red-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700', 'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700'];
  const colorClass = colors[review.id % colors.length];

  return (
    <div
      data-testid={`review-card-${review.id}`}
      className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 flex flex-col"
    >
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-12 h-12 rounded-full ${colorClass} flex items-center justify-center font-bold text-lg flex-shrink-0`}>
          {review.avatar}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h4 className="font-semibold text-slate-900 truncate">{review.name}</h4>
            <GoogleLogo />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <ReviewStars rating={review.rating} />
            <span className="text-xs text-slate-500">{review.date}</span>
          </div>
        </div>
      </div>
      <p className="text-slate-700 leading-relaxed text-sm flex-1">
        "{review.text}"
      </p>
    </div>
  );
};

const OverallRatingBanner = () => (
  <div className="inline-flex items-center gap-4 md:gap-6 bg-slate-50 border border-slate-200 rounded-2xl px-6 py-4 md:px-8 md:py-5">
    <GoogleLogo />
    <div className="text-left">
      <div className="flex items-center gap-2">
        <span className="text-3xl md:text-4xl font-bold text-slate-900">
          {googleReviews.overallRating}
        </span>
        <ReviewStars rating={5} />
      </div>
      <p className="text-sm text-slate-600 mt-0.5">
        Based on {googleReviews.totalReviews} Google reviews
      </p>
    </div>
  </div>
);

const ReviewsHeader = () => (
  <div className="text-center mb-12">
    <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
      Customer Reviews
    </span>
    <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
      What Our Customers Say
    </h2>
    <OverallRatingBanner />
  </div>
);

const ViewMoreLink = () => (
  <div className="text-center mt-12">
    <a
      href={googleReviews.profileUrl}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="view-all-reviews-btn"
      className="inline-flex items-center gap-2 bg-white border-2 border-slate-300 hover:border-blue-600 hover:text-blue-600 text-slate-700 px-8 py-3.5 rounded-lg font-semibold transition-all duration-200"
    >
      View All Reviews on Google
      <ExternalLink size={18} />
    </a>
  </div>
);

const ReviewsSection = () => (
  <section id="reviews" data-testid="reviews-section" className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <ReviewsHeader />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {googleReviews.reviews.map((review) => (
          <ReviewCard key={review.id} review={review} />
        ))}
      </div>
      <ViewMoreLink />
    </div>
  </section>
);

export default ReviewsSection;
